import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { scheduleApi } from '../api/endpoints.ts';
import { visitInstructionsApi, instructionsKey } from '../api/visit-instructions.ts';
import { useSession } from '../state/session.tsx';
import { usePlanFeature } from './plan-gate.tsx';
import { Alert, Button } from './ui.tsx';
import { formatDateAndTime } from '../i18n/datetime.ts';

export function InboxVisitInstruction({ customerId, messageId }: { customerId: string; messageId: string }) {
  const { can, user, company, currentLocation, impersonatedBy } = useSession();
  const scheduling = usePlanFeature('scheduling');
  if (!can('schedule:write') || !can('customers:read') || !scheduling) return null;
  const scope = JSON.stringify(instructionsKey(messageId, user, company, currentLocation?.id, impersonatedBy));
  return <InboxVisitInstructionForm key={scope} customerId={customerId} messageId={messageId} />;
}

function InboxVisitInstructionForm({ customerId, messageId }: { customerId: string; messageId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [visitId, setVisitId] = useState('');
  const requestKey = useRef<string | null>(null);
  const schedule = useQuery({
    queryKey: ['instruction-targets', customerId],
    queryFn: () => scheduleApi.customerSchedule(customerId),
    enabled: open,
  });
  const visits =
    schedule.data?.upcoming.filter((visit) => ['scheduled', 'confirmed', 'change_requested'].includes(visit.status)) ??
    [];
  const target = visitId || visits[0]?.id || '';
  const add = useMutation({
    mutationFn: () => {
      requestKey.current ??= crypto.randomUUID();
      return visitInstructionsApi.add(target, { requestKey: requestKey.current, text, messageId });
    },
    onSuccess: () => {
      setOpen(false);
      setText('');
      requestKey.current = null;
    },
  });
  return (
    <div className="mt-2">
      {add.isSuccess && <p role="status">{t('visitInstructions.saved')}</p>}
      {!open ? (
        <Button
          variant="secondary"
          onClick={() => {
            setText(window.getSelection()?.toString() ?? '');
            setOpen(true);
            add.reset();
          }}
        >
          {t('visitInstructions.addToVisit')}
        </Button>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <label className="field-label" htmlFor={`instruction-visit-${messageId}`}>
            {t('visitInstructions.visit')}
          </label>
          <select
            className="field-control"
            id={`instruction-visit-${messageId}`}
            value={target}
            disabled={add.isPending}
            onChange={(e) => {
              setVisitId(e.target.value);
              requestKey.current = null;
            }}
          >
            {visits.map((visit) => (
              <option key={visit.id} value={visit.id}>
                {formatDateAndTime(visit.scheduledDate, visit.startTimeLocal)} {visit.propertyAddress}
              </option>
            ))}
          </select>
          <p className="my-2 text-sm">{t('visitInstructions.approvalHint')}</p>
          <label className="field-label" htmlFor={`instruction-text-${messageId}`}>
            {t('visitInstructions.selectedText')}
          </label>
          <textarea
            className="field-control"
            id={`instruction-text-${messageId}`}
            value={text}
            rows={3}
            maxLength={5000}
            disabled={add.isPending}
            onChange={(e) => {
              setText(e.target.value);
              requestKey.current = null;
            }}
          />
          {schedule.isError && <Alert tone="error">{t('visitInstructions.loadVisitsError')}</Alert>}
          {schedule.data && visits.length === 0 && <p>{t('visitInstructions.noVisit')}</p>}
          <div className="mt-2 flex gap-2">
            <Button
              type="submit"
              busy={add.isPending}
              disabled={!text.trim() || !visits.some((visit) => visit.id === target)}
            >
              {t('visitInstructions.add')}
            </Button>
            <Button variant="secondary" disabled={add.isPending} onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
          </div>
          {add.isError && <Alert tone="error">{t('visitInstructions.saveError')}</Alert>}
        </form>
      )}
    </div>
  );
}
